import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import './CalculatorPage.css';
import ServiceList from './ServiceList';
// import BusinessSummary from './BusinessSummary'; // Removed import
import ProductsTab from './ProductsTab';
import OverheadsTab from './OverheadsTab';
import PricingCalculator from './PricingCalculator';
import StaffCards from './StaffCards'; // Changed from StaffManagement to StaffCards
import CalculationHistory from './CalculationHistory';

// Import only the services that exist
import serviceService from '../services/serviceService';
import productService from '../services/productService';
import categoryService from '../services/categoryService';
import businessService from '../services/businessService'; // Added business service
// import businessService from '../services/businessService'; // Removed import
import calculationService from '../services/calculationService';
import serviceCostService from '../services/serviceCostService';
import staffService from '../services/staffService';

const CalculatorPage = ({ activeTab: initialTab, customContent = null }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();

  // State variables
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [serviceDetails, setServiceDetails] = useState([]);
  const [staff, setStaff] = useState([]);
  const [overheads, setOverheads] = useState([]);
  const [businessSettings, setBusinessSettings] = useState(null); // Added business settings state
  const [activeTab, setActiveTab] = useState(initialTab || 'services');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vatRate, setVatRate] = useState(20);
  const [profitMargin, setProfitMargin] = useState(0.5);  // Ensure it's a float
  const [calculationHistory, setCalculationHistory] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [calculationResults, setCalculationResults] = useState(null);
  // Add this to track when services were last updated
  const [servicesUpdated, setServicesUpdated] = useState(Date.now());
  // Add this to track when staff were last updated
  const [staffUpdated, setStaffUpdated] = useState(Date.now());

  // Products and staff state
  const [newProduct, setNewProduct] = useState({ name: '', containerSize: 1, unit: 'Uses', totalCost: 0 });
  const [newStaff, setNewStaff] = useState({ name: '', role: '', rate: 0, hours_per_week: 0 });
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [currentStaff, setCurrentStaff] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);

  // Transform functions
  const transformServiceFromBackend = (serviceData) => ({
    id: serviceData.Service_ID,
    name: serviceData.Service_Name,
    category: serviceData.service_cat,
    time: serviceData.Service_Time,
    currentPrice: serviceData.Current_Cost || 0,
    payrate: serviceData.payrate,
    productCost: serviceData.product_cost || serviceData.productCost || serviceData.materialCost || serviceData.calculated_product_cost || 0,
    // Add these calculated cost fields
    total_cost: serviceData.total_cost || 0,
    recommended_price: serviceData.recommended_price || 0,
    product_cost: serviceData.product_cost || serviceData.productCost || serviceData.materialCost || serviceData.calculated_product_cost || 0,
    time_cost: serviceData.time_cost || 0,
    overhead_cost: serviceData.overhead_cost || 0,
    profit_margin: serviceData.profit_margin || 0,
    vat_rate: serviceData.vat_rate || 0,
    price_with_vat: serviceData.price_with_vat || 0,
    gross_profit: serviceData.gross_profit || 0,
    // Existing fields for backward compatibility
    rrp: serviceData.recommended_price || 0,
    calculatedPrice: serviceData.recommended_price || 0,
    grossProfit: serviceData.gross_profit || 0,
    timeCost: serviceData.time_cost || 0,
    overheadCost: serviceData.overhead_cost || 0,
    products: serviceData.details?.map(detail => ({
      id: detail.id,
      product_id: detail.Product_ID,
      name: detail.Product_Name,
      containerSize: detail.Pack_Qty,
      totalCost: detail.Price_Inc_Vat,
      usage: detail.Usage
    })) || []
  });

  const transformProductsFromBackend = (productsData) =>
    productsData.map(product => ({
      id: product.Product_ID,
      name: product.Product_Name,
      containerSize: product.qty || 1,
      totalCost: product.price || 0
    }));

  // Update tab when prop changes
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  // Tab navigation
  const navigateToTab = (tabName) => {
    setActiveTab(tabName);
    navigate(`/calculator/${tabName}`);
  };

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch services
        const servicesRes = await serviceService.getAllServices();
        console.log('Services response:', servicesRes);
        const transformedServices = servicesRes.data.map(service =>
          transformServiceFromBackend(service)
        );
        setServices(transformedServices);

        // Fetch products and other data
        const productsRes = await productService.getAllProducts();
        setProducts(transformProductsFromBackend(productsRes.data || []));

        // Fetch staff data
        try {
          const staffRes = await staffService.getStaff();
          setStaff(staffRes || []);
        } catch (staffErr) {
          console.error('Error fetching staff data:', staffErr);
          // Continue even if staff data fails
        }

        // Fetch business settings
        try {
          const businessRes = await businessService.getBusinessSettings();
          setBusinessSettings(businessRes.data);
          console.log('Business settings loaded in CalculatorPage:', businessRes.data);
        } catch (businessErr) {
          console.error('Error fetching business settings:', businessErr);
          // Continue even if business settings fail
        }

        // Removed business summary fetch
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Refresh services when tab changes or when services are updated
  useEffect(() => {
    if (activeTab === 'services') {
      const refreshServices = async () => {
        try {
          const servicesRes = await serviceService.getAllServices();
          const transformedServices = servicesRes.data.map(service =>
            transformServiceFromBackend(service)
          );
          setServices(transformedServices);
        } catch (err) {
          console.error('Error refreshing services:', err);
        }
      };

      refreshServices();
    }
  }, [activeTab, servicesUpdated]);

  // Refresh staff when tab changes to staff or when staff are updated
  useEffect(() => {
    if (activeTab === 'staff') {
      const refreshStaff = async () => {
        try {
          const staffRes = await staffService.getStaff();
          setStaff(staffRes || []);
        } catch (err) {
          console.error('Error refreshing staff:', err);
        }
      };

      refreshStaff();
    }
  }, [activeTab, staffUpdated]);

  // Group services by category
  const servicesByCategory = services.reduce((acc, service) => {
    const category = service.category || 'Uncategorised';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(service);
    return acc;
  }, {});

  // Handle edit service
  const handleEditService = (serviceId) => {
    navigate(`/calculator/service-editor/${serviceId}`);
    // Force refresh when returning to this page
    setTimeout(() => setServicesUpdated(Date.now()), 500);
  };

  // Handle delete service
  const handleDeleteService = async (serviceId) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await serviceService.deleteService(serviceId);
        // Refresh services after deletion
        setServicesUpdated(Date.now()); // Trigger refresh
      } catch (err) {
        console.error('Error deleting service:', err);
        alert('Failed to delete service. Please try again.');
      }
    }
  };

  // Add new service
  const handleAddService = () => {
    navigate('/calculator/service-editor/new');
    // Force refresh when returning
    setTimeout(() => setServicesUpdated(Date.now()), 500);
  };

  // PHASE 2: Handle service duplication
  const handleDuplicateService = async (serviceId, newName) => {
    try {
      // For now, we'll simulate the duplication
      // You can implement the actual backend call later
      console.log(`Duplicating service ${serviceId} with new name: ${newName}`);
      
      // Simulate API call
      alert(`Service "${newName}" will be created! (Backend integration needed)`);
      
      // TODO: Replace with actual API call
      // const response = await serviceService.duplicateService(serviceId, newName);
      // if (response.success) {
      //   setServicesUpdated(Date.now());
      //   alert(`Service "${newName}" created successfully!`);
      // }
      
      // For now, just refresh the list
      setServicesUpdated(Date.now());
    } catch (err) {
      console.error('Error duplicating service:', err);
      alert('Failed to duplicate service. Please try again.');
    }
  };

  // Calculate service pricing
  const calculateServicePricing = async (serviceId, options = {}) => {
    try {
      setSelectedService(serviceId);
      console.log("Calculating pricing for service:", serviceId);

      // Ensure profit margin and vat rate are floats
      const result = await serviceCostService.calculateServiceCosts(serviceId, {
        profit_margin: parseFloat(options.profit_margin || 1), // Removed businessSummary dependency
        vat_rate: parseFloat(options.vat_rate || 20),
        useTargetApproach: true // Explicitly use target approach
      });

      console.log("Calculation result:", result.data);
      setCalculationResults(result.data);
      return result.data;
    } catch (err) {
      console.error('Error calculating service pricing:', err);
      return null;
    }
  };

  // Format time for display
  const formatTime = (minutes) => {
    if (!minutes) return '0 mins';

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours === 0) {
      return `${remainingMinutes} mins`;
    } else if (remainingMinutes === 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      return `${hours} hour${hours > 1 ? 's' : ''} ${remainingMinutes} minutes`;
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="calculator-container">
      <h1>Hair & Beauty Calculator</h1>

      {/* Business Summary */}
      {/* <BusinessSummary businessSummary={businessSummary} /> */} {/* Removed rendering */}

      <div className="calculator-tabs">
        <button
          className={`tab-button ${activeTab === 'services' ? 'active' : ''}`}
          onClick={() => navigateToTab('services')}
        >
          Services
        </button>
        <button
          className={`tab-button ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => navigateToTab('products')}
        >
          Products
        </button>
        <button
          className={`tab-button ${activeTab === 'overheads' ? 'active' : ''}`}
          onClick={() => navigateToTab('overheads')}
        >
          Overheads
        </button>
        <button
          className={`tab-button ${activeTab === 'staff' ? 'active' : ''}`}
          onClick={() => navigateToTab('staff')}
        >
          Staff
        </button>
        <button
          className={`tab-button ${activeTab === 'calculator' ? 'active' : ''}`}
          onClick={() => navigateToTab('calculator')}
        >
          Pricing Calculator
        </button>
        <button
          className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => navigateToTab('history')}
        >
          History
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Custom content if provided */}
        {customContent && customContent}

        {/* Services Tab */}
        {activeTab === 'services' && !customContent && (
          <ServiceList
            servicesByCategory={servicesByCategory}
            onEditService={handleEditService}
            onDeleteService={handleDeleteService}
            onCalculateService={calculateServicePricing}
            onDuplicateService={handleDuplicateService}
            calculationResults={calculationResults}
            selectedService={selectedService}
            formatTime={formatTime}
            onAddService={handleAddService}
            settings={businessSettings}  // Use real business settings instead of hardcoded
            stylists={staff}
          />
        )}

        {/* Products Tab */}
        {activeTab === 'products' && !customContent && (
          <ProductsTab
            products={products}
            setProducts={setProducts}
            newProduct={newProduct}
            setNewProduct={setNewProduct}
            editingProduct={editingProduct}
            setEditingProduct={setEditingProduct}
          />
        )}

        {/* Overheads Tab */}
        {activeTab === 'overheads' && !customContent && (
          <OverheadsTab
            overheads={overheads}
            setOverheads={setOverheads}
            staff={staff}
            setStaff={setStaff}
            newStaff={newStaff}
            setNewStaff={setNewStaff}
            showStaffForm={showStaffForm}
            setShowStaffForm={setShowStaffForm}
            currentStaff={currentStaff}
            setCurrentStaff={setCurrentStaff}
          />
        )}

        {/* Staff Management Tab - NOW USING BEAUTIFUL CARDS! */}
        {activeTab === 'staff' && !customContent && (
          <StaffCards />
        )}
        
        {/* History Tab */}
        {activeTab === 'history' && !customContent && (
          <CalculationHistory />
        )}

        {/* Pricing Calculator Tab */}
        {activeTab === 'calculator' && !customContent && (
          <PricingCalculator
            services={services}
            vatRate={vatRate}
            setVatRate={setVatRate}
            profitMargin={profitMargin}
            setProfitMargin={setProfitMargin}
            calculateServicePricing={calculateServicePricing}
            calculationResults={calculationResults}
            calculationHistory={calculationHistory}
          />
        )}
      </div>
    </div>
  );
};

export default CalculatorPage;